import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api from '../utils/api'
import { CaseDetailProvider } from '../components/case-detail/CaseDetailContext'
import CaseHeader from '../components/case-detail/CaseHeader'
import LegalCase from '../components/case-detail/LegalCase'
import CaseDocuments from '../components/case-detail/CaseDocuments'
import CaseParticipants from '../components/case-detail/CaseParticipants'
import FinanceTable from '../components/case-detail/FinanceTable'
import EmailReminder from '../components/case-detail/EmailReminder'
import ProgressTracker from '../components/case-detail/ProgressTracker'
import CaseDelete from '../components/case-detail/CaseDelete'
import CaseReschedule from '../components/case-detail/CaseReschedule'
import CaseChangeTime from '../components/case-detail/CaseChangeTime'
import CaseCancel from '../components/case-detail/CaseCancel'
import AddActivity from '../components/case-detail/AddActivity'
import useCaseActivityPageLoading from '../components/activities/useCaseActivityPageLoading'
import TransferCase from '../components/case-detail/TransferCase'
import Disclosure from '../components/case-detail/Disclosure'
import DraftInvoices from '../components/case-detail/DraftInvoices'
import MediatorNotes from '../components/case-detail/MediatorNotes'
import Signatures from '../components/case-detail/Signatures'
import CreateInvoiceModal from '../components/case-detail/CreateInvoiceModal'
import EditInvoiceModal from '../components/case-detail/EditInvoiceModal'
import ActivityLog from '../components/case-detail/ActivityLog'
// Note: OnboardingLinks and TransferAlertBanner are rendered inside
// CaseHeader (which is the Scale-portal layout). They're not imported
// here directly to avoid duplicating them.

/**
 * CaseDetail — page-level orchestrator. Mirrors Scale's
 * CaseManagement/CaseDetail/index.js layout (12-col grid: 9 main / 3 right).
 */

function Toast({ toast, onDismiss }) {
    useEffect(() => {
        if (!toast) return
        const t = setTimeout(onDismiss, 4000)
        return () => clearTimeout(t)
    }, [toast, onDismiss])
    if (!toast) return null
    const styleByType = {
        success: 'bg-green-600 text-white',
        error: 'bg-red-600 text-white',
        warning: 'bg-yellow-500 text-white',
    }
    return (
        <div className={`fixed bottom-6 right-6 px-4 py-2 rounded-md shadow-lg text-sm z-[10000] ${styleByType[toast.type] || 'bg-gray-800 text-white'}`}>
            {toast.message}
        </div>
    )
}

function CaseDetailInner() {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const activityRequestsReady = useCaseActivityPageLoading(new URLSearchParams(location.search).get('focus') === 'activities')

    const [caseData, setCaseData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [draftInvoices, setDraftInvoices] = useState([])
    const [showDrafts, setShowDrafts] = useState(false)
    const [showCreateInvoice, setShowCreateInvoice] = useState(false)
    const [showEditInvoice, setShowEditInvoice] = useState(false)
    const [childMediators, setChildMediators] = useState([])

    const refreshCaseData = useCallback(async () => {
        try {
            const { data } = await api.get(`/cases/${id}`)
            setCaseData(data?.data || null)
        } catch (err) {
            console.error('Failed to fetch case', err)
            setToast({ type: 'error', message: 'Failed to load case' })
        }
    }, [id])

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                const [caseRes, draftsRes, childRes] = await Promise.all([
                    api.get(`/cases/${id}`),
                    api.get(`/cases/${id}/draft-invoices`).catch(() => ({ data: { data: [] } })),
                    api.get('/cases/utility/child-mediators').catch(() => ({ data: { data: [] } })),
                ])
                if (cancelled) return
                setCaseData(caseRes?.data?.data || null)
                setDraftInvoices(draftsRes?.data?.data || [])
                setChildMediators(childRes?.data?.data || [])
            } catch (err) {
                if (!cancelled) setToast({ type: 'error', message: 'Failed to load case' })
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => { cancelled = true }
    }, [id])

    // Refresh after returning from /hourly-billing or /update-invoice
    useEffect(() => {
        if (location.state?.refresh) {
            refreshCaseData()
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state?.refresh, location.pathname, navigate, refreshCaseData])

    const handleDeleteDraft = async (draftId) => {
        try {
            // Use the scoped route under /:id/. Falls back to legacy alias on 404.
            try {
                await api.delete(`/cases/${id}/hourly-billing/drafts/${draftId}`)
            } catch (err) {
                if (err?.response?.status === 404) {
                    await api.delete(`/cases/draft-invoices/${draftId}`)
                } else {
                    throw err
                }
            }
            setDraftInvoices((prev) => prev.filter((d) => d.id !== draftId))
            setToast({ type: 'success', message: 'Draft deleted' })
        } catch (err) {
            setToast({ type: 'error', message: 'Failed to delete draft' })
        }
    }

    const mediatorOptions = useMemo(() => (
        childMediators
            .filter((m) => m.user_id !== caseData?.mediatorId)
            .map((m) => ({
                value: m.user_id,
                label: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email,
                email: m.email,
            }))
    ), [childMediators, caseData])

    const payments = caseData?.payments || []
    const hasInvoices = payments.length > 0
    const isCancelled = caseData?.rawStatus === 'cancelled'
    // GSC cases have no plaintiff/defendant parties (the firm is the only party).
    // Scale hides the party-detail cards for GSC (CaseDetail/index.js:450,464,698)
    // and shows only the firm's Progress Tracker + traffic lights + Finances.
    const isGsc = caseData?.is_gsc_case === true || (caseData?.party_emails?.length || 0) > 0
    // Scale's `areBothInvoicesNotPending`, ported from the mediator portal's
    // PRODUCTION branch (CaseDetail/index.js:76-100), which is the behaviour
    // this portal is required to match.
    //
    // Disabled only when BOTH sides have an invoice AND both are strictly
    // 'paid'. `paid_manually` stays editable — deliberately, and commented out
    // on the Scale side too.
    //
    // The row read per side is `plaintiffPayments[0] || plaintiff.payment`,
    // Scale's exact expression. Those arrays are raw DB rows, so `.status` is
    // the lowercase column value, not FinanceTable's capitalised display copy.
    //
    // DELIBERATELY NOT PORTED: Scale's uat-v2 adds a second branch that also
    // disables the button when a case is billed hourly and every row in the
    // merged table is settled. That fix is real, but it is NOT on the mediator
    // portal's production branch, so taking it here would make this portal
    // stricter than the mediator portal users actually see. Revisit when Scale
    // releases uat-v2 — at which point this becomes a two-branch check again.
    const plaintiffInvoice = caseData?.plaintiffPayments?.[0] || caseData?.plaintiff?.payment
    const defendantInvoice = caseData?.defendentPayments?.[0] || caseData?.defendant?.payment
    const allPaid = !!plaintiffInvoice && !!defendantInvoice
        && plaintiffInvoice.status === 'paid' && defendantInvoice.status === 'paid'

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
        )
    }

    if (!caseData) {
        return <div className="p-8 text-center text-sm text-gray-500">Case not found.</div>
    }

    return (
        <CaseDetailProvider initialCaseData={caseData} refreshCaseData={refreshCaseData}>
            <div data-case-detail-page data-case-detail-ready={activityRequestsReady ? 'true' : 'false'} className="p-4 md:p-6">
                <div className="w-full space-y-3">
                    {/* CaseHeader internally renders TransferAlertBanner +
                        OnboardingLinks (Scale-portal layout). */}
                    <CaseHeader
                        onToast={setToast}
                        backLabel="Cases"
                        backTo="/cases"
                        onCaseRefresh={refreshCaseData}
                    />

                    <div className="grid grid-cols-12 gap-3">
                        {/* Main column */}
                        <div className="col-span-12 lg:col-span-9 space-y-3">
                            {/* Scale parity: party-detail cards are hidden for GSC
                                cases (no plaintiff/defendant parties). */}
                            {!isGsc && <LegalCase />}
                            {!isGsc && <CaseDocuments onToast={setToast} onRefresh={refreshCaseData} />}
                            {!isGsc && <CaseParticipants onToast={setToast} onRefresh={refreshCaseData} />}

                            {/* Finances */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-5 md:px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
                                    <div>
                                        <div className="text-base font-semibold text-gray-900">Finances</div>
                                        <p className="text-xs text-gray-500">Invoices from both parties</p>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        {draftInvoices.length > 0 && (
                                            <button
                                                type="button"
                                                disabled={isCancelled}
                                                title={isCancelled ? 'Case is canceled!' : ''}
                                                onClick={() => setShowDrafts(true)}
                                                className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
                                            >
                                                Draft Invoices ({draftInvoices.length})
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            disabled={isCancelled}
                                            title={isCancelled ? 'Case is canceled!' : ''}
                                            onClick={() => navigate(`/hourly-billing/${id}`, { state: { backgroundLocation: location } })}
                                            className="px-3 py-1.5 text-xs font-medium bg-[#173336] text-white hover:bg-[#0d2226] rounded-md disabled:opacity-50"
                                        >
                                            Create Invoice
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isCancelled || !hasInvoices || allPaid}
                                            title={
                                                isCancelled ? 'Case is canceled!' :
                                                !hasInvoices ? 'Create an invoice before editing' :
                                                allPaid ? 'Cannot edit invoice when both plaintiff and defendant invoices are paid' : 'Edit Invoice'
                                            }
                                            onClick={() => navigate(`/update-invoice/${id}`, { state: { backgroundLocation: location } })}
                                            className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50"
                                        >
                                            Edit Invoice
                                        </button>
                                    </div>
                                </div>
                                <FinanceTable
                                    onToast={setToast}
                                    onRefresh={refreshCaseData}
                                    mediatorUserId={caseData.mediator?.userId}
                                />
                            </div>

                            {/* Signatures — inline widget showing the
                                signing request status for this case
                                (parties, captured signatures count,
                                per-row Resend). Mirrors the Scale
                                portal's case-detail Signatures card so
                                CMs see signing status without leaving
                                the case page. */}
                            <Signatures onToast={setToast} />

                            <MediatorNotes onToast={setToast} />
                        </div>

                        {/* Right sidebar */}
                        <div className="col-span-12 lg:col-span-3 space-y-3">
                            <ProgressTracker />
                            <ActivityLog />
                            {!isGsc && <EmailReminder onToast={setToast} />}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-5 py-4 border-b border-gray-200">
                                    <h5 className="text-base font-semibold text-gray-900">Case Actions</h5>
                                    <p className="text-xs text-gray-500 mt-0.5">Manage case operations</p>
                                </div>
                                <div className="px-5 py-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Add Activity — renders nothing unless this
                                            case's mediator has the feature, so the grid
                                            is unchanged for everyone else. */}
                                        {caseData?.activity_available === true && (
                                            <div className="w-full flex items-stretch">
                                                <AddActivity caseData={caseData} onSaved={refreshCaseData} />
                                            </div>
                                        )}
                                        <div className="w-full flex items-stretch"><CaseDelete onToast={setToast} /></div>
                                        <div className="w-full flex items-stretch"><CaseReschedule onToast={setToast} onRescheduled={refreshCaseData} /></div>
                                        <div className="w-full flex items-stretch"><CaseChangeTime onToast={setToast} onChanged={refreshCaseData} /></div>
                                        <div className={`w-full flex items-stretch ${mediatorOptions.length === 0 ? 'col-span-2' : ''}`}>
                                            <CaseCancel onToast={setToast} onCancelled={refreshCaseData} />
                                        </div>
                                        {mediatorOptions.length > 0 && (
                                            <div className="w-full flex items-stretch">
                                                <TransferCase
                                                    onToast={setToast}
                                                    onTransferred={() => navigate('/cases')}
                                                    mediatorOptions={mediatorOptions}
                                                />
                                            </div>
                                        )}
                                        <div className={`w-full flex items-stretch ${mediatorOptions.length === 0 ? 'col-span-2' : ''}`}>
                                            <Disclosure onToast={setToast} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DraftInvoices
                isOpen={showDrafts}
                onClose={() => setShowDrafts(false)}
                invoices={draftInvoices}
                onDelete={handleDeleteDraft}
                onEdit={(inv) => {
                    // Scale parity: navigate to /hourly-billing/:caseId?draft_invoice=:uuid
                    setShowDrafts(false)
                    navigate(`/hourly-billing/${id}?draft_invoice=${encodeURIComponent(inv.uuid)}`, { state: { backgroundLocation: location } })
                }}
            />

            <CreateInvoiceModal
                isOpen={showCreateInvoice}
                onClose={() => setShowCreateInvoice(false)}
                caseData={caseData}
                onSuccess={refreshCaseData}
                onToast={setToast}
            />

            <EditInvoiceModal
                isOpen={showEditInvoice}
                onClose={() => setShowEditInvoice(false)}
                caseData={caseData}
                onSuccess={refreshCaseData}
                onToast={setToast}
            />

            <Toast toast={toast} onDismiss={() => setToast(null)} />
        </CaseDetailProvider>
    )
}

export default function CaseDetail() {
    return <CaseDetailInner />
}
